import { useEffect, useMemo, useState } from 'react';

import { FormikProps, FormikValues, useFormik } from 'formik';
import { DateTime } from 'luxon';

import {
  AdminLegendVariant,
  getLongTermContractAttributes,
  PharmacistLegendVariant,
  setLongTermContractAttributes,
  WorkshiftStatus,
} from '@pharmaplan/common';

import useGenerateContractRange from '../../LongTermContract/useGenerateContractRange';
import useViewEditContract from '../../workshift/useViewEditContract';
import useExpressBookingTypes from './useExpressBookingTypes';
import { resetDialog } from '../../../reducers/dialogReducer';
import { useAppSelector } from '../../useAppSelector';
import { useAppDispatch } from '../../useAppDispatch';
import { nameTypes } from '../../../components/SelfService/Workshifts/WorkshiftForm/ViewEditContract/ViewEditContractTime/ViewEditContractTime';

const useExpressBookingLongTermModal = (
  type: AdminLegendVariant | PharmacistLegendVariant,
  formFormik: FormikProps<any>,
  isUpdate: boolean,
) => {
  const dispatch = useAppDispatch();
  const { detailsApiMap } = useExpressBookingTypes();
  const { details } = detailsApiMap[type as keyof typeof detailsApiMap] ?? {};
  const isEditingBooking = isUpdate && type === AdminLegendVariant.booked;

  const { getInitialShiftAttributes } = useGenerateContractRange();
  const allShifts = isEditingBooking ? null : details?.allShifts ?? null;
  const bookings = detailsApiMap[AdminLegendVariant.booked].details.bookings ?? [];
  const onlyAvailableShifts = useMemo(() =>
    allShifts?.filter((item) =>
      item.status !== WorkshiftStatus.Booked), [allShifts]);

  const { intervalsInitial, getFormattedObj, generateTimeIntervals } = useViewEditContract();

  const longTermAttributes = useAppSelector(getLongTermContractAttributes);
  const {
    startDate: savedStartDate,
    endDate: savedEndDate,
    defaultEndTime: savedDefaultEndTime,
    defaultStartTime: savedDefaultStartTime,
    shiftContractAttributes,
  } = longTermAttributes ?? {};

  const [selectedIds, setSelectedIds] = useState<Array<string>>(
    formFormik.values?.workshiftIds ?? [],
  );

  const isAllSelected = selectedIds?.length === onlyAvailableShifts?.length;

  const onSubmit = (values: FormikValues) => {
    const { startDate, endDate, defaultStartTime, defaultEndTime } = values ?? {};

    const formattedObj = getFormattedObj(values);
    dispatch(
      setLongTermContractAttributes({
        shiftContractAttributes: Object.values(formattedObj),
        startDate,
        endDate,
        defaultEndTime,
        defaultStartTime,
      }),
    );

    if (!isEditingBooking) {
      formFormik.setFieldValue('workshiftIds', selectedIds);
    }
    dispatch(resetDialog());
  };

  const formik = useFormik<FormikValues>({
    initialValues: {
      startDate: savedStartDate ?? null,
      endDate: savedEndDate ?? null,
      defaultStartTime:
        savedDefaultStartTime
        ?? DateTime.now().set({ hour: 9, minute: 0, second: 0 }).toJSDate(),
      defaultEndTime:
        savedDefaultEndTime
        ?? DateTime.now().set({ hour: 17, minute: 0, second: 0 }).toJSDate(),
    },
    onSubmit,
  });

  const hasInvalidBookingTimes = isEditingBooking && bookings.some((shift) => {
    const date = shift.startDate.split('T')[0];
    const { values } = formik;
    const start = values[`${date}|${nameTypes.start}`];
    const end = values[`${date}|${nameTypes.end}`];

    return !(start instanceof Date && end instanceof Date && end > start);
  });

  const handleCheckboxClick = (shiftId: string) =>
    () => {
      if (selectedIds.includes(shiftId)) {
        setSelectedIds(selectedIds.filter((item) =>
          item !== shiftId));
      } else {
        setSelectedIds((prev) =>
          [...prev, shiftId]);
      }
    };

  const handleClose = () => {
    dispatch(resetDialog());
  };

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(() =>
        (onlyAvailableShifts ?? []).map((item) =>
          item.workShiftId ?? ''));
    }
  };

  const onDateChange = (
    startDate: DateTime | string,
    endDate: DateTime | string,
  ) => {
    const shiftAttributes = getInitialShiftAttributes({
      startDate: startDate as DateTime,
      endDate: endDate as DateTime,
      startTime:
        DateTime.fromJSDate(formik.values.defaultStartTime as Date).toISO({
          includeOffset: false,
        }) ?? '',
      endTime:
        DateTime.fromJSDate(formik.values.defaultEndTime as Date).toISO({
          includeOffset: false,
        }) ?? '',
    });

    if (shiftAttributes?.length) {
      formik.setValues({
        startDate: formik.values.startDate,
        endDate: formik.values.endDate,
        defaultStartTime: formik.values.defaultStartTime,
        defaultEndTime: formik.values.defaultEndTime,
        ...generateTimeIntervals(shiftAttributes)(
          formik.values?.defaultStartTime as Date,
          formik.values?.defaultEndTime as Date,
        ),
      });

      dispatch(
        setLongTermContractAttributes({
          ...longTermAttributes,
          shiftContractAttributes: shiftAttributes ?? [],
        }),
      );
    }
  };

  useEffect(() => {
    if (shiftContractAttributes?.length) {
      formik.setValues({
        startDate: formik.values.startDate,
        endDate: formik.values.endDate ?? null,
        defaultStartTime:
          formik.values.defaultStartTime,
        defaultEndTime:
          formik.values.defaultEndTime,
        ...intervalsInitial(shiftContractAttributes ?? []),

      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shiftContractAttributes, savedEndDate, savedStartDate]);

  const shiftData = isUpdate ? allShifts : shiftContractAttributes;

  return {
    formik,
    handleCheckboxClick,
    handleClose,
    handleSelectAll,
    disabled: isEditingBooking
      ? bookings.length === 0 || hasInvalidBookingTimes
      : selectedIds.length === 0,
    isAllSelected,
    data: isEditingBooking ? bookings : shiftData,
    selectedIds,
    onDateChange,
  };
};

export default useExpressBookingLongTermModal;
